"use client";

import {
  BarChart2,
  Book,
  BookOpen,
  Briefcase,
  ChevronDown,
  Code2,
  FileText,
  Newspaper,
  Settings,
  Shield,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import type { TeamSwitcherRef } from "@/components/global-team-switcher";
import { UserMenu } from "@/components/layout/user-menu";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { AuthModal } from "../auth/auth-modal";

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const LandingHeader = () => {
  const { data: session } = useSession();
  const [scrolled, setScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const teamSwitcherRef = useRef<TeamSwitcherRef>(null);

  const cookieStore = typeof window !== "undefined" ? document.cookie : "";
  const orgNameMatch = cookieStore.match(/organisationName=([^;]+)/);
  const organizationName = orgNameMatch
    ? decodeURIComponent(orgNameMatch[1])
    : "";

  const userName = session?.user?.displayName || session?.user?.username || "";
  const userInitials = getInitials(userName);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all",
        scrolled
          ? "bg-black/30 backdrop-blur-md border-b border-white/10"
          : "bg-transparent"
      )}
    >
      <div className="container relative mx-auto flex h-20 items-center justify-between px-4">
        {/* Left side: Logo */}
        <div className="flex items-center">
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/nudj-logos/Nudj-large-light.png"
              alt="Nudj Logo"
              width={72}
              height={24}
              priority
            />
          </Link>
        </div>

        {/* Center: Admin Panel */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {session ? (
            <Link href="/admin">
              <Button
                variant="default"
                className="backdrop-blur-md bg-white/20 hover:bg-white/30 text-white border border-white/30 shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
              >
                <Settings className="h-4 w-4 mr-2" />
                Take me to the control room
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() => setShowAuthModal(true)}
              className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
            >
              Get Started
            </Button>
          )}
        </div>

        {/* Right side: Docs + Profile */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 hidden sm:inline-flex text-white/90 hover:text-white hover:bg-white/20 transition-all duration-200"
            asChild
          >
            <Link href="https://docs.nudj.cx/introduction" target="_blank">
              <FileText className="h-4 w-4" />
              <span>Docs</span>
            </Link>
          </Button>
          {session && (
            <UserMenu
              userName={userName}
              userInitials={userInitials}
              organizationName={organizationName}
            />
          )}
        </div>
      </div>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </header>
  );
};

export default LandingHeader;
